import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Info, TrendingUp } from 'lucide-react';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Label,
  LabelList,
  Legend,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  type TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import { EstimationAccuracyPeriod, EstimationAccuracyResponse } from '../../../services/apiService';
import {
  DYNAMICS_RECHARTS_TOOLTIP_CONTENT_STYLE,
  DYNAMICS_RECHARTS_TOOLTIP_LABEL_STYLE,
} from '../quality/dynamics/dynamicsRechartsTooltip';
import { useEstimationUnit } from './estimationUnit';
import { EstimationUnitToggle } from './EstimationUnitToggle';

type Basis = 'release' | 'quarter';
type WindowSize = 6 | 12 | 'all';

interface Props {
  data: EstimationAccuracyResponse;
  /** Optional seed for the Release/Quarter toggle. Defaults to `quarter` since
   * the trend reads better over a longer horizon than a single release. */
  initialBasis?: Basis;
  /** Project-wide target deviation percentage (default 10). */
  targetDeviationPct?: number;
}

/**
 * Picks an opinionated default window: keep ranges short enough to read but
 * never collapse to a single bar — that's what produces the "useless tiny
 * chart" look on freshly bootstrapped products.
 */
function pickInitialWindow(periods: EstimationAccuracyPeriod[]): WindowSize {
  if (periods.length >= 12) return 12;
  if (periods.length >= 6) return 6;
  return 'all';
}

/* Colour contracts mirror .deviation-over (red) / .deviation-under (green)
 * already used across the Estimation accuracy tab and tables, so a user that
 * already learned "red = went over estimate" doesn't relearn anything. */
const COLOR_OVER = '#ef4444'; // logged > estimate (overran)
const COLOR_UNDER = '#22c55e'; // logged < estimate (finished under)
/** Same purple tone the original "avg |deviation|" line used so the user's
 * eye keeps the same anchor even though the metric has changed. */
const COLOR_DEVIATION_LINE = '#a855f7';
const COLOR_TARGET = '#22c55e';
const COLOR_ZERO = 'rgba(148, 163, 184, 0.55)';

/** Format a unit-converted value with sign and the active short suffix
 * (`h` or `d`). Switches to compact `kX` rendering only for hours; days
 * stay below 4 digits in practice so 1 decimal is always readable. */
function formatUnitSigned(v: number | null | undefined, suffix: 'h' | 'd'): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const sign = v > 0 ? '+' : v < 0 ? '−' : '';
  const abs = Math.abs(v);
  if (suffix === 'h' && abs >= 1000) return `${sign}${(abs / 1000).toFixed(1)}k ${suffix}`;
  if (suffix === 'h' && abs >= 100) return `${sign}${Math.round(abs)} ${suffix}`;
  return `${sign}${abs.toFixed(1)} ${suffix}`;
}

function formatUnitAxis(v: number, suffix: 'h' | 'd'): string {
  const abs = Math.abs(v);
  if (suffix === 'h' && abs >= 1000) return `${v < 0 ? '−' : ''}${Math.round(abs / 100) / 10}k`;
  // Recharts may emit float ticks on small ranges; keep them tidy.
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

/**
 * Custom label for signed bars. Recharts' built-in `position="top"` always
 * draws above the bar's top edge, which for negative bars is the zero line —
 * the label would land on top of the axis. We position labels just outside
 * the bar regardless of sign and colour them to match.
 *
 * `suffix` is closed over by the parent component so the same renderer keeps
 * using the active "h" / "d" without prop-drilling through Recharts internals.
 */
function makeHourBarLabel(suffix: 'h' | 'd'): React.FC<{
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  value?: number | string;
}> {
  const Cmp: React.FC<{
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    value?: number | string;
  }> = ({ x = 0, y = 0, width = 0, height = 0, value }) => {
    const numeric = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numeric)) return null;
    if (numeric === 0) return null;
    const isNeg = numeric < 0;
    const cx = x + width / 2;
    const labelY = isNeg ? y + height + 14 : y - 6;
    return (
      <text
        x={cx}
        y={labelY}
        textAnchor="middle"
        fontSize={11}
        fontWeight={600}
        fill={isNeg ? COLOR_UNDER : COLOR_OVER}
      >
        {formatUnitSigned(numeric, suffix)}
      </text>
    );
  };
  return Cmp;
}

interface PointShape {
  period: string;
  storyCount?: number;
  totalEstimateHours?: number;
  totalLoggedHours?: number;
  deviationHours?: number;
  deviationPct?: number | null;
  within10PercentPct?: number;
  avgDeviationPct?: number | null;
}

function formatPctSigned(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  if (v === 0) return '0%';
  return `${v > 0 ? '+' : '−'}${Math.abs(v)}%`;
}

function makeTrendTooltip(
  suffix: 'h' | 'd',
  targetDeviationPct: number
): React.FC<TooltipProps<number, string>> {
  const TT: React.FC<TooltipProps<number, string>> = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    const point = (payload[0]?.payload ?? null) as PointShape | null;
    if (!point) return null;
    const devUnits = point.deviationHours ?? 0;
    const devPct = point.deviationPct ?? null;
    const isOver = devUnits > 0;
    const isUnder = devUnits < 0;
    return (
      <div style={DYNAMICS_RECHARTS_TOOLTIP_CONTENT_STYLE}>
        <div style={DYNAMICS_RECHARTS_TOOLTIP_LABEL_STYLE}>{label}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.78rem' }}>
          <div>
            <span
              style={{
                color: isOver ? COLOR_OVER : isUnder ? COLOR_UNDER : 'inherit',
                fontWeight: 600,
              }}
            >
              {formatUnitSigned(devUnits, suffix)}
            </span>
            <span style={{ opacity: 0.7, marginLeft: 6 }}>
              deviation ({isOver ? 'overran estimate' : isUnder ? 'under estimate' : 'on target'})
            </span>
          </div>
          <div>
            <span
              style={{
                color:
                  devPct == null
                    ? 'inherit'
                    : devPct > 0
                      ? COLOR_OVER
                      : devPct < 0
                        ? COLOR_UNDER
                        : 'inherit',
                fontWeight: 600,
              }}
            >
              {formatPctSigned(devPct)}
            </span>
            <span style={{ color: COLOR_DEVIATION_LINE, marginLeft: 6 }}>
              deviation (% of estimation)
            </span>
          </div>
          <div>
            <span style={{ fontWeight: 600 }}>{point.within10PercentPct ?? 0}%</span>
            <span style={{ opacity: 0.7, marginLeft: 6 }}>
              of stories within ±{targetDeviationPct}%
            </span>
          </div>
          <div style={{ fontSize: '0.7rem', opacity: 0.6, marginTop: 4 }}>
            {point.storyCount ?? 0} stories ·{' '}
            {point.totalEstimateHours != null
              ? `${point.totalEstimateHours.toFixed(suffix === 'd' ? 1 : 0)}${suffix} est`
              : '—'}{' '}
            ·{' '}
            {point.totalLoggedHours != null
              ? `${point.totalLoggedHours.toFixed(suffix === 'd' ? 1 : 0)}${suffix} logged`
              : '—'}
          </div>
        </div>
      </div>
    );
  };
  return TT;
}

export const EstimationTrendCard: React.FC<Props> = ({
  data,
  initialBasis = 'quarter',
  targetDeviationPct = 10,
}) => {
  const { suffix, parenSuffix, toUnitValue } = useEstimationUnit();
  const [basis, setBasis] = useState<Basis>(initialBasis);
  // Default collapsed — the trend is a "deep-dive" supplement to the KPI
  // cards and the period detail above it. The user opens it when they want
  // to see the long-running picture.
  const [expanded, setExpanded] = useState(false);
  const toggle = () => setExpanded((v) => !v);

  // The API returns periods newest-first. The trend reads left→right, so we
  // reverse before slicing the window. Memo on (data, basis) so we don't
  // produce a fresh array on every render.
  const orderedAll = useMemo(() => {
    const src = basis === 'release' ? (data.byRelease ?? []) : (data.byQuarter ?? []);
    return [...src].reverse();
  }, [data, basis]);
  const [windowSize, setWindowSize] = useState<WindowSize>(() => pickInitialWindow(orderedAll));

  const rawPoints = useMemo(() => {
    if (windowSize === 'all') return orderedAll;
    return orderedAll.slice(-windowSize);
  }, [orderedAll, windowSize]);

  /** Apply the active unit conversion once to the data array. Recharts then
   *  uses the converted values for bar geometry, axis ticks, and tooltip
   *  payloads — no per-axis hacks needed. */
  const points = useMemo(
    () =>
      rawPoints.map((p) => ({
        ...p,
        deviationHours: p.deviationHours != null ? toUnitValue(p.deviationHours) : p.deviationHours,
        totalEstimateHours:
          p.totalEstimateHours != null ? toUnitValue(p.totalEstimateHours) : p.totalEstimateHours,
        totalLoggedHours:
          p.totalLoggedHours != null ? toUnitValue(p.totalLoggedHours) : p.totalLoggedHours,
      })),
    [rawPoints, toUnitValue]
  );

  const tooFew = points.length < 2;

  const HourBarLabel = useMemo(() => makeHourBarLabel(suffix), [suffix]);
  const TrendTooltip = useMemo(
    () => makeTrendTooltip(suffix, targetDeviationPct),
    [suffix, targetDeviationPct]
  );
  const axisTickFormatter = useMemo(() => (v: number) => formatUnitAxis(v, suffix), [suffix]);

  /** Symmetric axis so the zero line of bars and zero of the % line align
   * visually in the middle of the chart — that alignment is what makes "the
   * bar and the line tell the same story" obvious at a glance. */
  const leftAxisDomain = useMemo<[number, number]>(() => {
    let absMax = 0;
    for (const p of points) {
      const v = typeof p.deviationHours === 'number' ? p.deviationHours : 0;
      if (Math.abs(v) > absMax) absMax = Math.abs(v);
    }
    if (absMax === 0) return [-10, 10];
    const pad = Math.max(absMax * 0.18, 5);
    const bound = Math.ceil(absMax + pad);
    return [-bound, bound];
  }, [points]);

  /** Same symmetric strategy on the right axis. Always keep the ±target
   * zone visible (`baseline >= 15`), and adapt to data otherwise so a single
   * outlier doesn't flatten everything. */
  const rightAxisDomain = useMemo<[number, number]>(() => {
    let absMax = 0;
    for (const p of points) {
      const v = typeof p.deviationPct === 'number' ? p.deviationPct : 0;
      if (Math.abs(v) > absMax) absMax = Math.abs(v);
    }
    const baseline = Math.max(absMax, 15);
    let bound: number;
    if (baseline <= 30) bound = 40;
    else if (baseline <= 100) bound = Math.ceil(baseline / 20) * 20 + 10;
    else bound = Math.ceil(baseline / 50) * 50;
    return [-bound, bound];
  }, [points]);

  return (
    <div
      className={`chart-card estimation-trend-card${expanded ? ' is-expanded' : ' is-collapsed'}`}
    >
      <div className="chart-header estimation-trend-card__header">
        <div
          role="button"
          tabIndex={0}
          className="estimation-trend-card__toggle"
          aria-expanded={expanded}
          aria-controls="estimation-trend-body"
          onClick={toggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggle();
            }
          }}
        >
          {expanded ? (
            <ChevronDown size={20} className="estimation-trend-card__chevron" />
          ) : (
            <ChevronRight size={20} className="estimation-trend-card__chevron" />
          )}
          <div className="estimation-trend-card__title">
            <h3>
              <TrendingUp size={18} className="estimation-trend-card__title-icon" />
              Estimation accuracy trend
              <span
                className="stat-hint-icon"
                title="Bars: Deviation in hours per period — above zero means the team logged more than estimated, below zero means they logged less. Line: Deviation (%) — the same number as the period summary above (totalLogged − totalEstimate) ÷ totalEstimate. The faint green band marks the ±{targetDeviationPct}% accuracy target zone."
                aria-label="About Estimation accuracy trend"
                onClick={(e) => e.stopPropagation()}
              >
                <Info size={14} />
              </span>
            </h3>
            <p>
              {expanded
                ? `${basis === 'release' ? 'Per release (fix version)' : 'Per quarter'} · ${
                    points.length
                  } period${points.length === 1 ? '' : 's'} shown`
                : 'Click to expand the long-running accuracy trend.'}
            </p>
          </div>
        </div>
        {expanded && (
          <div className="estimation-trend-card__controls" onClick={(e) => e.stopPropagation()}>
            <EstimationUnitToggle />
            <div className="capacity-mode-toggle estimation-trend-card__basis" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={basis === 'release'}
                className={basis === 'release' ? 'active' : ''}
                onClick={() => setBasis('release')}
              >
                Release
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={basis === 'quarter'}
                className={basis === 'quarter' ? 'active' : ''}
                onClick={() => setBasis('quarter')}
              >
                Quarter
              </button>
            </div>
            <div className="estimation-trend-card__window" role="group" aria-label="Window size">
              {([6, 12, 'all'] as const).map((opt) => (
                <button
                  key={String(opt)}
                  type="button"
                  className={`estimation-trend-card__window-pill${
                    windowSize === opt ? ' is-active' : ''
                  }`}
                  onClick={() => setWindowSize(opt)}
                  disabled={opt !== 'all' && orderedAll.length < (opt as number)}
                  title={
                    opt === 'all'
                      ? 'Show all available periods'
                      : `Show last ${opt} ${basis === 'release' ? 'releases' : 'quarters'}`
                  }
                >
                  {opt === 'all' ? 'All' : `Last ${opt}`}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {!expanded ? null : tooFew ? (
        <div id="estimation-trend-body" className="estimation-trend-card__empty">
          <p>Need at least 2 {basis === 'release' ? 'releases' : 'quarters'} to show a trend.</p>
          <p className="estimation-trend-card__empty-hint">
            Once another {basis === 'release' ? 'release' : 'quarter'} closes, the chart will fill
            in automatically.
          </p>
        </div>
      ) : (
        <div id="estimation-trend-body" className="estimation-trend-card__body">
          <div className="estimation-trend-card__chart">
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={points} margin={{ top: 24, right: 28, bottom: 12, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.22} />
                <XAxis
                  dataKey="period"
                  tick={{ fontSize: 12 }}
                  interval={0}
                  angle={-15}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  yAxisId="left"
                  domain={leftAxisDomain}
                  tick={{ fontSize: 12 }}
                  tickFormatter={axisTickFormatter}
                  width={56}
                >
                  <Label
                    value={`Deviation ${parenSuffix}`}
                    angle={-90}
                    position="insideLeft"
                    offset={10}
                    style={{ fontSize: 11, fill: 'var(--color-text-secondary, #94a3b8)' }}
                  />
                </YAxis>
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={rightAxisDomain}
                  unit="%"
                  tick={{ fontSize: 12 }}
                  width={52}
                />
                <Tooltip
                  content={<TrendTooltip />}
                  cursor={{ fill: 'rgba(148, 163, 184, 0.08)' }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 12 }}
                  formatter={(value, entry) => {
                    /* Bars carry sign-meaning per Cell (red over / green under), so
                     * the legend label for the metric itself is shown in the same
                     * neutral slate as the matching "Deviation (h)" label in the
                     * Period summary card — anchoring the two views as one metric. */
                    const isHourBar =
                      (entry as { dataKey?: string } | undefined)?.dataKey === 'deviationHours';
                    const color = isHourBar
                      ? 'var(--color-text-secondary, #94a3b8)'
                      : (entry?.color ?? 'inherit');
                    return <span style={{ color }}>{value}</span>;
                  }}
                />
                <ReferenceArea
                  yAxisId="right"
                  y1={-targetDeviationPct}
                  y2={targetDeviationPct}
                  fill={COLOR_TARGET}
                  fillOpacity={0.07}
                  stroke="none"
                />
                <ReferenceLine yAxisId="left" y={0} stroke={COLOR_ZERO} strokeWidth={1.25} />
                <ReferenceLine
                  yAxisId="right"
                  y={targetDeviationPct}
                  stroke={COLOR_TARGET}
                  strokeDasharray="4 4"
                  label={{
                    value: `+${targetDeviationPct}%`,
                    position: 'right',
                    fill: COLOR_TARGET,
                    fontSize: 11,
                  }}
                />
                <ReferenceLine
                  yAxisId="right"
                  y={-targetDeviationPct}
                  stroke={COLOR_TARGET}
                  strokeDasharray="4 4"
                  label={{
                    value: `−${targetDeviationPct}%`,
                    position: 'right',
                    fill: COLOR_TARGET,
                    fontSize: 11,
                  }}
                />
                <Bar
                  yAxisId="left"
                  dataKey="deviationHours"
                  name={`Deviation ${parenSuffix}`}
                  radius={[4, 4, 4, 4]}
                  maxBarSize={42}
                  /* Bar's own `fill` is what Recharts paints into the Legend's
                   * swatch icon. Per-bar colour comes from the <Cell> children
                   * below, so this only affects the legend chip — keeping it
                   * neutral grey to match the Period summary's "Deviation (h)"
                   * label. */
                  fill="var(--color-text-secondary, #94a3b8)"
                >
                  {points.map((p) => (
                    <Cell
                      key={p.period}
                      fill={(p.deviationHours ?? 0) >= 0 ? COLOR_OVER : COLOR_UNDER}
                    />
                  ))}
                  <LabelList dataKey="deviationHours" content={<HourBarLabel />} />
                </Bar>
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="deviationPct"
                  name="Deviation (%)"
                  stroke={COLOR_DEVIATION_LINE}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  connectNulls
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <p className="estimation-trend-card__legend-hint">
            <span className="estimation-trend-card__swatch estimation-trend-card__swatch--over" />{' '}
            Above zero — team logged more than estimated.{' '}
            <span className="estimation-trend-card__swatch estimation-trend-card__swatch--under" />{' '}
            Below zero — under estimate. The purple line is the same <em>Deviation (%)</em> shown in
            the period summary above; aim to keep it inside the faint green ±{targetDeviationPct}%
            band.
          </p>
        </div>
      )}
    </div>
  );
};
